import type { ReactElement } from 'react';

import { cn } from '../lib/cn';

import type { KpiVariant } from './kpi-stats-card';

export interface KpiRatioCardProps {
  label: string;
  value: string;
  deltaPct: number | null;
  /** Cor do VALOR. positive=verde, negative=vermelho, neutral=ink. */
  variant: KpiVariant;
  /** Mostra ou não a linha de delta percentual. Default true. */
  showDelta?: boolean;
  /** Quando true, delta % colore por sinal. Default false (cinza). */
  coloredDelta?: boolean;
  /** Linha curta abaixo do valor (ex: "≈ R$ 2.750,00 · cotação do período"). */
  subtitle?: string;
  className?: string;
}

function formatDelta(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1).replace('.', ',')}%`;
}

export function KpiRatioCard({
  label,
  value,
  deltaPct,
  variant,
  showDelta = true,
  coloredDelta = false,
  subtitle,
  className,
}: KpiRatioCardProps): ReactElement {
  const deltaText = deltaPct === null ? '—' : formatDelta(deltaPct);

  const deltaColorClass =
    coloredDelta && deltaPct !== null && deltaPct !== 0
      ? deltaPct > 0
        ? 'text-success'
        : 'text-error'
      : 'text-ink-muted';

  // Cor do VALOR = sinal do próprio valor (variant), independente do delta.
  const valueColorClass =
    variant === 'negative' ? 'text-error' : variant === 'positive' ? 'text-success' : 'text-ink';

  // Tamanho do valor por comprimento, pra valores grandes não estourarem o card
  // (estes cards são estreitos, col-span-2). Mobile folgado, desktop (sm+) mais
  // agressivo. Mesma escala do KpiStatsCard pra consistência visual.
  const valueSizeClass =
    value.length <= 12 ? 'text-4xl' : value.length <= 15 ? 'text-3xl' : 'text-2xl';
  const valueSizeClassDesktop =
    value.length <= 9
      ? 'sm:text-4xl'
      : value.length <= 13
        ? 'sm:text-3xl'
        : value.length <= 17
          ? 'sm:text-2xl'
          : 'sm:text-xl';

  return (
    <article
      className={cn(
        'bg-surface/60 hover:bg-surface group border-border-subtle/30 relative flex min-h-[140px] flex-col justify-between gap-2 overflow-hidden rounded-3xl border px-6 py-5 backdrop-blur-xl transition-all duration-300',
        className,
      )}
    >
      <div className="text-ink-muted relative flex items-center gap-2 text-sm font-medium">
        <span className="truncate">{label}</span>
        {showDelta ? (
          <span className={cn('text-[13px] font-semibold', deltaColorClass)}>{deltaText}</span>
        ) : null}
      </div>
      <div
        className={cn(
          'relative leading-none font-bold tracking-tight whitespace-nowrap',
          valueSizeClass,
          valueSizeClassDesktop,
          valueColorClass,
        )}
      >
        {value}
      </div>
      {subtitle ? <p className="text-ink-muted truncate text-xs">{subtitle}</p> : null}
    </article>
  );
}
